import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './UseCasesPage.css';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

function JourneyStory({ story, index }) {
  return (
    <motion.article
      className="uc-journey"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6 }}
    >
      <div className="uc-journey__head">
        <div className="uc-journey__eyebrow">
          <span className="uc-journey__index">Journey {String(index + 1).padStart(2, '0')}</span>
          <span className="uc-journey__dot" aria-hidden="true">•</span>
          <span className="uc-journey__industry">{story.industry}</span>
          <span className="uc-journey__dot" aria-hidden="true">•</span>
          <span className={`uc-journey__complexity uc-journey__complexity--${story.complexity.toLowerCase().replace(/\s+/g, '-')}`}>
            {story.complexity} Complexity
          </span>
        </div>
        <h3 className="uc-journey__usecase">{story.useCase}</h3>
        <p className="uc-journey__hero">
          <span className="uc-journey__hero-label">Customer:</span>{' '}
          {story.hero}
        </p>
        <p className="uc-journey__context">{story.context}</p>
      </div>

      <div className="uc-ba-grid">
        <section className="uc-ba-panel uc-ba-panel--before">
          <header className="uc-ba-panel__head">
            <span className="uc-ba-panel__tag uc-ba-panel__tag--before">BEFORE</span>
            <h4 className="uc-ba-panel__title">Stuck in pilot for {story.timeline.stuck}</h4>
            <p className="uc-ba-panel__subtitle">{story.beforeSummary}</p>
          </header>

          <div className="uc-ba-panel__body">
            <div className="uc-ba-block">
              <div className="uc-ba-block__label">Original architecture</div>
              <ul className="uc-stack-list uc-stack-list--before">
                {story.beforeStack.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="uc-ba-block">
              <div className="uc-ba-block__label">Why it never reached production</div>
              <ul className="uc-pain-list">
                {story.beforePain.map((item, i) => (
                  <li key={i}>
                    <span className="uc-pain-list__heading">{item.heading}</span>
                    <span className="uc-pain-list__text">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="uc-ba-panel uc-ba-panel--after">
          <header className="uc-ba-panel__head">
            <span className="uc-ba-panel__tag uc-ba-panel__tag--after">AFTER &mdash; ON CEREBRALMACHINES</span>
            <h4 className="uc-ba-panel__title">Live in production in {story.timeline.live}</h4>
            <p className="uc-ba-panel__subtitle">{story.afterSummary}</p>
          </header>

          <div className="uc-ba-panel__body">
            <div className="uc-ba-block">
              <div className="uc-ba-block__label">CerebralMachines architecture</div>
              <ul className="uc-stack-list uc-stack-list--after">
                {story.afterStack.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="uc-ba-block">
              <div className="uc-ba-block__label">What changed for the customer</div>
              <ul className="uc-outcome-list">
                {story.afterOutcomes.map((item, i) => (
                  <li key={i}>
                    <span className="uc-outcome-list__heading">{item.heading}</span>
                    <span className="uc-outcome-list__text">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>

      <div className="uc-results-strip">
        <div className="uc-metric-tile">
          <div className="uc-metric-tile__label">Accuracy</div>
          <div className="uc-metric-tile__row">
            <span className="uc-metric-tile__before">{story.metrics.accuracy[0]}</span>
            <span className="uc-metric-tile__arrow" aria-hidden="true">&rarr;</span>
            <span className="uc-metric-tile__after">{story.metrics.accuracy[1]}</span>
          </div>
        </div>
        <div className="uc-metric-tile">
          <div className="uc-metric-tile__label">Workflow speed</div>
          <div className="uc-metric-tile__row">
            <span className="uc-metric-tile__after">{story.metrics.speedup} faster end-to-end</span>
          </div>
        </div>
        <div className="uc-metric-tile">
          <div className="uc-metric-tile__label">Cost / transaction</div>
          <div className="uc-metric-tile__row">
            <span className="uc-metric-tile__before">{story.metrics.cost[0]}</span>
            <span className="uc-metric-tile__arrow" aria-hidden="true">&rarr;</span>
            <span className="uc-metric-tile__after">{story.metrics.cost[1]}</span>
          </div>
        </div>
        <div className="uc-metric-tile uc-metric-tile--savings">
          <div className="uc-metric-tile__label">Cost saved / txn</div>
          <div className="uc-metric-tile__row">
            <span className="uc-metric-tile__savings">{story.metrics.savedPct}</span>
            <span className="uc-metric-tile__savings-abs">({story.metrics.savedAbs})</span>
          </div>
        </div>
        <div className="uc-results-strip__timeline">
          <span className="uc-results-strip__timeline-label">Time-to-production</span>
          <span className="uc-results-strip__timeline-value">
            Stuck {story.timeline.stuck} <span aria-hidden="true">&rarr;</span> Live in {story.timeline.live}
          </span>
          <span className="uc-results-strip__timeline-team">{story.timeline.team}</span>
        </div>
      </div>
    </motion.article>
  );
}

function UseCasesPage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const journeys = [
    {
      industry: 'Banking',
      useCase: 'AML & Suspicious Activity Reports',
      complexity: 'Extreme',
      hero: 'The Director of AML Operations at a Tier-1 North American Bank.',
      context:
        'Her team triages roughly 18,000 alerts a day across retail, commercial, and correspondent banking. Every missed SAR is a regulatory event, every false positive burns analyst hours, and every model decision has to survive an audit by the FRB, OCC, and FinCEN reviewers.',
      beforeSummary:
        'A RAG-and-LLM pipeline stitched on top of the existing AML stack. Demoed beautifully. Could not be put in front of a real analyst, let alone a regulator.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Pinecone vector DB over policy + typology corpus',
        'OpenAI embeddings + cross-encoder reranker',
        'Unstructured / Azure Document Intelligence for SAR narratives',
        'LangChain orchestration with custom prompt templates',
        'Sanctions + 3 KYC vendors stitched in app code',
        'NeMo / custom guardrails layer',
        'Bolt-on rules engine for typology overrides',
        'HITL review queue in ServiceNow',
        'Ragas-style eval harness, ad-hoc'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost made it unusable.',
          text:
            'End-to-end alert triage was too slow to fit inside the analyst queue, only 71% accurate against the gold set, and $0.42 per transaction. At 18k alerts/day that was ~$2.8M/yr in inference alone, with worse accuracy than the rules engine it was meant to replace.'
        },
        {
          heading: 'It never made it into an analyst\u2019s daily routine.',
          text:
            'The end-to-end workflow was so slow that analysts could not absorb it into their queue without falling behind. The tool became a parallel toy, not the system of record. Adoption stayed under 10%.'
        },
        {
          heading: 'The volume math did not work.',
          text:
            '~6.5M alerts/yr \u00d7 $0.42 = unfundable. Finance refused to expand beyond the pilot region until cost-per-alert dropped by an order of magnitude.'
        },
        {
          heading: 'Explainability did not satisfy the regulator.',
          text:
            'The chain of thought was a free-text rationale that changed run-to-run. Internal audit and the OCC examiners could not reconcile two identical alerts producing two different narratives.'
        },
        {
          heading: 'Human-in-the-loop was not feasible.',
          text:
            '100% review of 18k/day was impossible; sampling defeated the entire automation thesis. Risk would not approve any auto-disposition.'
        },
        {
          heading: 'Stuck in pilot for 8 months.',
          text:
            'Five vendor PoCs, three architecture rewrites, two LLM swaps. Risk and compliance still had a hard "no" on production.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. Same alert feed, same typology library, same SAR narrative templates. Deterministic, explainable, and cheap enough to run on every alert.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over AML typologies, policies, and SAR templates',
        'Deterministic process algorithms for the typology decision tree',
        'Verifiable chain of logic per alert (regulator-readable)',
        'Automated semantic layer over KYC, sanctions, and transaction feeds',
        'Native HITL hooks for exception-only review',
        'No new GPUs, no fine-tuning, no retraining cadence'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 71% to 96%.',
          text:
            'The deterministic process graph applies the typology rules the way the policy actually reads. False positives dropped, true SARs were caught earlier, and the gold-set score finally cleared the risk threshold.'
        },
        {
          heading: 'Workflow runs ~5\u00d7 faster end-to-end.',
          text:
            'Analysts now triage alerts inside their normal queue cadence. CerebralMachines became the system of record, not a side tool.'
        },
        {
          heading: 'Cost went from $0.42 to $0.11 per alert (74% saved).',
          text:
            'Annual inference spend dropped from ~$2.8M to ~$0.7M. Finance unblocked the expansion to all geographies in the same review.'
        },
        {
          heading: 'Audit and the OCC could read every decision.',
          text:
            'The verifiable chain of logic is identical run-to-run, cites the exact policy clause, and exports as a regulator-ready PDF. Internal audit signed off on auto-disposition for low-risk alerts.'
        },
        {
          heading: 'Human-in-the-loop became exception-only.',
          text:
            'Analysts now see only the cases that genuinely need judgment. Throughput per analyst tripled without adding headcount.'
        },
        {
          heading: 'Released to production in 24 days.',
          text:
            'One platform engineer + one AML SME. No GPU cluster, no model retraining, no new vendors. The 8-month pilot shipped in under 4 weeks.'
        }
      ],
      metrics: {
        accuracy: ['71%', '96%'],
        speedup: '5\u00d7',
        cost: ['$0.42', '$0.11'],
        savedPct: '74%',
        savedAbs: '$0.31'
      },
      timeline: {
        stuck: '8 months',
        live: '24 days',
        team: '1 platform engineer + 1 AML SME'
      }
    },
    {
      industry: 'Healthcare',
      useCase: 'Prior Authorization',
      complexity: 'Very High',
      hero: 'The VP of Utilization Management at a National Health Plan.',
      context:
        'His UM operation processes ~32,000 prior authorization requests per day across commercial, Medicare Advantage, and Medicaid lines. Each request has to be reconciled against payer policy, plan benefits, and clinical criteria \u2014 and turnaround clocks are regulated to the hour.',
      beforeSummary:
        'A RAG copilot for UM nurses with policy PDFs in a vector store. Looked promising in demos, fell over on the real volume and the real audit trail.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Weaviate vector DB over payer policy PDFs',
        'OpenAI embeddings + BM25 hybrid retriever',
        'Manual policy parser for SharePoint policy library',
        'LangChain orchestration with prompt templates per LOB',
        'InterQual / MCG criteria mirrored as JSON copies',
        'Custom guardrails for HIPAA / PHI redaction',
        'UM nurse review queue in Salesforce Health Cloud',
        'Audit log dump to S3, no replay capability'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost broke the SLA.',
          text:
            'End-to-end PA decisioning was too slow for the daily queue, only 68% accurate against medical-director adjudications, and $0.58 per transaction. The CMS turnaround clock and per-case cost ceiling were both blown.'
        },
        {
          heading: 'UM nurses would not adopt it.',
          text:
            'The end-to-end workflow could not fit inside their daily queue, and they did not trust an answer they could not trace to the exact policy paragraph. The tool was used as a search box, not a decision tool.'
        },
        {
          heading: 'The volume math was unfundable.',
          text:
            '~11.7M PAs/yr \u00d7 $0.58 = ~$6.8M/yr. Finance asked for a 5x cost cut before approving expansion, and the model could not deliver it.'
        },
        {
          heading: 'Explainability did not pass medical-director review.',
          text:
            'Two identical requests produced two different rationales. Medical directors and external auditors could not validate the chain of reasoning, so every auto-approval had to be re-reviewed.'
        },
        {
          heading: 'Human-in-the-loop was infeasible at this scale.',
          text:
            '100% nurse review of 32k/day defeated the automation case. Sampling left regulators uncomfortable.'
        },
        {
          heading: 'Stuck in pilot for 7 months.',
          text:
            'Two model swaps, one vector DB migration, an entire policy re-ingestion project. Compliance still would not green-light auto-approval for any LOB.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. The exact same payer policy library, the same InterQual/MCG criteria, the same Salesforce queue \u2014 just a deterministic, explainable reasoning core underneath.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over payer policy + plan benefits + clinical criteria',
        'Deterministic process algorithms for medical-necessity decisioning',
        'Verifiable chain of logic per PA (medical-director-readable)',
        'Automated semantic layer over EHR, eligibility, and benefits feeds',
        'Native HIPAA-aware controls; PHI never leaves the boundary',
        'Native exception routing into the existing Salesforce queue'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 68% to 94%.',
          text:
            'Medical-necessity decisions now match the medical-director gold set. Approvals that should approve auto-approve; denials carry the exact policy citation.'
        },
        {
          heading: 'Workflow runs ~4\u00d7 faster end-to-end.',
          text:
            'UM nurses moved CerebralMachines into their primary workflow. Turnaround inside the CMS clock is no longer a concern, including expedited cases.'
        },
        {
          heading: 'Cost went from $0.58 to $0.14 per PA (76% saved).',
          text:
            'Annual inference spend dropped from ~$6.8M to ~$1.6M. Finance approved national rollout across all lines of business.'
        },
        {
          heading: 'Medical directors and auditors finally trust the trace.',
          text:
            'The chain of logic cites the exact policy clause and criterion line, identical across runs. External audit moved from sampling to spot-check.'
        },
        {
          heading: 'Human review is now exception-only.',
          text:
            'Nurses see only the cases the deterministic logic flags as ambiguous. Their throughput nearly tripled without adding headcount.'
        },
        {
          heading: 'Released to production in 21 days.',
          text:
            'One platform engineer + one UM clinical SME. The 7-month pilot went live in 3 weeks, on the existing infrastructure.'
        }
      ],
      metrics: {
        accuracy: ['68%', '94%'],
        speedup: '4\u00d7',
        cost: ['$0.58', '$0.14'],
        savedPct: '76%',
        savedAbs: '$0.44'
      },
      timeline: {
        stuck: '7 months',
        live: '21 days',
        team: '1 platform engineer + 1 UM clinical SME'
      }
    },
    {
      industry: 'Healthcare',
      useCase: 'UM & Medical-Necessity Appeals',
      complexity: 'Extreme',
      hero: 'The Director of Clinical Appeals at a Regional Medicaid MCO.',
      context:
        'His team handles ~4,500 appeals and grievances per month, each one a multi-document case spanning the original denial, clinical records, payer policy, NCD/LCD references, and state-specific Medicaid rules. Reversals trigger compliance reporting; under-reviews trigger lawsuits.',
      beforeSummary:
        'A RAG \u201cappeals copilot\u201d that summarized cases for clinicians. Useful as a reading aid, useless as a decision system, and impossible to defend in front of a state auditor.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Qdrant vector DB over denial letters, policies, and clinical guidelines',
        'OpenAI embeddings + cross-encoder reranker',
        'Document intake parsers for fax, PDF, and Epic exports',
        'LangChain orchestration with appeal-type-specific chains',
        'Mirrored copies of NCD/LCD + state Medicaid rules in JSON',
        'Custom HIPAA / PHI guardrails',
        'Epic / EHR connector for clinical context',
        'Appeal queue in a homegrown case-management tool'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost were all wrong.',
          text:
            'End-to-end appeal review was the slowest workflow in the portfolio, only 64% accurate against the medical-director reference, and $0.81 per case. The most expensive use case in the program \u2014 and the worst on accuracy.'
        },
        {
          heading: 'Clinicians could not put it in their daily flow.',
          text:
            'On a 30-case daily queue, the end-to-end review time killed adoption. The tool was used to draft summaries, not to make calls.'
        },
        {
          heading: 'The volume math was unfundable.',
          text:
            '~54k appeals/yr \u00d7 $0.81 = ~$44k/mo just on inference, with worse accuracy than the existing manual process. Expansion was paused indefinitely.'
        },
        {
          heading: 'Explainability could not survive a state audit.',
          text:
            'Reasoning rationales drifted across runs. State auditors and grievance examiners could not reconcile two identical fact patterns producing different outcomes. Every auto-decision had to be re-reviewed.'
        },
        {
          heading: 'Human-in-the-loop was the de-facto operating model.',
          text:
            'Every case still went through a clinician end-to-end, so the \u201cautomation\u201d delivered no real labor savings.'
        },
        {
          heading: 'Stuck in pilot for 9 months.',
          text:
            'Two RAG vendors, three prompt rewrites, one fine-tune attempt. Compliance still would not approve auto-decisioning for any appeal class.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. Same case feed, same policy and NCD/LCD library, same Epic integration \u2014 with deterministic appeal logic and a per-case audit trail.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over denial letters, clinical records, NCDs/LCDs, and state Medicaid rules',
        'Deterministic process algorithms for appeal class \u2192 decision logic',
        'Verifiable chain of logic per appeal (state-auditor-readable)',
        'Automated semantic layer over Epic, claims, and grievance systems',
        'Native HIPAA-aware controls and full PHI containment',
        'Exception-only routing into the existing case-management tool'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 64% to 93%.',
          text:
            'Decisions match the medical-director reference. Reversals are caught earlier; defensible upholds are documented with the exact clinical and policy citations.'
        },
        {
          heading: 'Workflow runs ~5\u00d7 faster end-to-end.',
          text:
            'Clinicians work cases live, not in batch. Same-day disposition is now the norm even for the most complex appeals.'
        },
        {
          heading: 'Cost went from $0.81 to $0.19 per appeal (77% saved).',
          text:
            'Monthly inference spend dropped by more than 4x. The MCO unlocked rollout across every appeal class.'
        },
        {
          heading: 'State auditors got a deterministic record.',
          text:
            'Identical fact patterns produce identical outcomes, with citations to the exact NCD/LCD section and clinical evidence line. Grievance examiners stopped re-reviewing every case.'
        },
        {
          heading: 'Human-in-the-loop is now exception-only.',
          text:
            'Clinicians focus on genuinely ambiguous cases instead of every case. Per-clinician throughput more than doubled.'
        },
        {
          heading: 'Released to production in 26 days.',
          text:
            'One platform engineer + one appeals clinical SME. The 9-month pilot went live in under 4 weeks, with no infrastructure changes.'
        }
      ],
      metrics: {
        accuracy: ['64%', '93%'],
        speedup: '5\u00d7',
        cost: ['$0.81', '$0.19'],
        savedPct: '77%',
        savedAbs: '$0.62'
      },
      timeline: {
        stuck: '9 months',
        live: '26 days',
        team: '1 platform engineer + 1 appeals clinical SME'
      }
    },
    {
      industry: 'Telecom',
      useCase: 'Network Change Management & Troubleshooting',
      complexity: 'Very High',
      hero: 'The Head of Network Operations at a Tier-1 Wireless Carrier.',
      context:
        'His NOC processes ~9,000 change requests and ~22,000 incident tickets a month across RAN, core, and transport. A bad change at 3 a.m. is a national outage; a missed correlation is a regulatory filing. Every action has to be defensible to network engineering, security, and the FCC.',
      beforeSummary:
        'A RAG-based \u201ctroubleshooting assistant\u201d wired into the NOC console. Confidently wrong on the production network. NetOps stopped trusting it after the first false-positive shutdown recommendation.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Pinecone vector DB over runbooks, MOPs, and vendor docs',
        'OpenAI embeddings + custom reranker',
        'Document parsers for vendor PDFs and Confluence runbooks',
        'LangChain orchestration with tool-use for ticket and CMDB lookups',
        'Network CMDB + change ticket system + on-call paging integrations',
        'Custom guardrails for change windows + blast-radius checks',
        'Side rules engine for regulatory + security gates',
        'Eval harness driven off historical post-mortems'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost were all wrong for NOC tempo.',
          text:
            'End-to-end ticket triage ran far slower than NOC tempo allows, only 72% accurate against the senior-engineer gold set, and $0.36 per transaction. NOC tempo is sub-5-second; the workflow could not get there.'
        },
        {
          heading: 'On-call engineers stopped using it.',
          text:
            'After two false-positive change-rollback recommendations, NetOps lost trust. The tool was disabled in incident bridges within a quarter.'
        },
        {
          heading: 'The volume math did not pencil.',
          text:
            '~370k tickets/yr \u00d7 $0.36 = ~$133k/yr just for inference, with worse-than-human accuracy and no pathway to autonomous action.'
        },
        {
          heading: 'Explainability could not pass change-advisory or FCC review.',
          text:
            'Free-text rationales drifted across runs. CAB chairs would not approve any automated change, and security would not allow tool-use into production CMDB writes.'
        },
        {
          heading: 'Human-in-the-loop was infeasible at incident tempo.',
          text:
            'Engineers cannot pause a P1 to read a 1,200-token rationale; they need a deterministic answer or none.'
        },
        {
          heading: 'Stuck in pilot for 6 months.',
          text:
            'Three vendor swaps, two prompt rewrites, one model retrain. CAB and security still had a hard \u201cno\u201d on autonomous action.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. Same runbooks, same CMDB, same ticketing \u2014 deterministic change-and-incident logic with verifiable, replayable decisions.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over runbooks, MOPs, vendor docs, and CMDB topology',
        'Deterministic process algorithms for change-window, blast-radius, and rollback logic',
        'Verifiable chain of logic per recommendation (CAB- and FCC-readable)',
        'Automated semantic layer over CMDB, ticketing, and on-call paging',
        'Native exception routing into the on-call rotation',
        'No GPUs added; runs on the existing edge fleet'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 72% to 95%.',
          text:
            'Recommendations match the senior-engineer gold set. False-positive rollbacks effectively went to zero on the validation set.'
        },
        {
          heading: 'Workflow runs ~4\u00d7 faster end-to-end.',
          text:
            'NOC engineers see answers at incident tempo. The assistant is now active inside live incident bridges.'
        },
        {
          heading: 'Cost went from $0.36 to $0.09 per ticket (75% saved).',
          text:
            'Annual inference spend dropped from ~$133k to ~$33k. The CFO approved nationwide rollout across RAN, core, and transport.'
        },
        {
          heading: 'CAB and security finally signed off.',
          text:
            'Every recommendation carries a deterministic chain of logic citing the exact runbook step and CMDB constraint. Auto-action is permitted inside pre-approved change classes.'
        },
        {
          heading: 'Human-in-the-loop is exception-only.',
          text:
            'Engineers act on the unusual cases, not every ticket. Mean-time-to-resolve dropped materially across major outage classes.'
        },
        {
          heading: 'Released to production in 22 days.',
          text:
            'One NOC platform engineer + one senior network SME. The 6-month pilot shipped in 3 weeks, with no new vendors and no GPU spend.'
        }
      ],
      metrics: {
        accuracy: ['72%', '95%'],
        speedup: '4\u00d7',
        cost: ['$0.36', '$0.09'],
        savedPct: '75%',
        savedAbs: '$0.27'
      },
      timeline: {
        stuck: '6 months',
        live: '22 days',
        team: '1 NOC platform engineer + 1 senior network SME'
      }
    },
    {
      industry: 'Insurance',
      useCase: 'Commercial Claims Adjudication',
      complexity: 'High',
      hero: 'The Chief Claims Officer at a Top-10 Commercial P&C Insurer.',
      context:
        'His organization adjudicates ~14,000 commercial claims a day across property, GL, auto, and workers\u2019 comp. Each claim is a stack of policy docs, ISO/Verisk feeds, adjuster notes, vendor invoices, and SIU referrals. Loss-leakage on misadjudication is measured in tens of millions per year.',
      beforeSummary:
        'A RAG \u201cadjuster copilot\u201d trained on policy language and historical claims. Helpful for first-pass summarization, unsafe for adjudication and unloved by SIU and audit.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Weaviate vector DB over policy forms and claims history',
        'OpenAI embeddings + hybrid BM25 retriever',
        'Document parsers for FNOL, photos-to-text OCR, and vendor invoices',
        'LangChain orchestration per LOB',
        'Policy admin system + ISO / Verisk feeds + adjuster desktop integrations',
        'SIU referral form + side fraud-rules engine',
        'Custom guardrails for state-specific UCSPA / unfair-claims rules',
        'Audit log dump to S3, no replay capability'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost were all unsuitable for adjudication.',
          text:
            'End-to-end adjudication was too slow for live FNOL, only 74% accurate against the senior-adjuster gold set, and $0.29 per transaction. Below the threshold needed to even reach the auto-adjudication review.'
        },
        {
          heading: 'Adjusters used it for summaries, not decisions.',
          text:
            'Coverage decisions still ran through the adjuster desk. The tool added a step rather than replacing one, so cycle time barely moved.'
        },
        {
          heading: 'The volume math limited rollout.',
          text:
            '~5.1M claims/yr \u00d7 $0.29 = ~$1.5M/yr in inference. Combined with no measurable loss-leakage win, the program could not justify expansion past one LOB.'
        },
        {
          heading: 'Explainability did not satisfy state regulators.',
          text:
            'Two identical claims produced two different coverage rationales. Internal audit and state DOI examiners could not reconcile decisions; SIU\u2019s fraud signals were buried in free text.'
        },
        {
          heading: 'Human-in-the-loop was the entire workflow.',
          text:
            'Every coverage call still went through an adjuster, so the \u201cautomation\u201d was effectively assistive only.'
        },
        {
          heading: 'Stuck in pilot for 7 months.',
          text:
            'Three model versions, two retrieval strategies, one vendor swap. The auto-adjudication review never opened.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. Same policy admin, same ISO/Verisk feeds, same adjuster desktop \u2014 a deterministic coverage and fraud reasoning core that audit, SIU, and DOI can all read.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over policy forms, endorsements, and claims history',
        'Deterministic process algorithms for coverage \u2192 limits \u2192 deductible \u2192 fraud-signal logic',
        'Verifiable chain of logic per claim (DOI- and SIU-readable)',
        'Automated semantic layer over policy admin, ISO/Verisk, and adjuster notes',
        'State-by-state UCSPA / unfair-claims controls native to the policy graph',
        'Exception-only routing into existing adjuster + SIU queues'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 74% to 96%.',
          text:
            'Coverage decisions match the senior-adjuster gold set. Loss-leakage from misadjudication dropped substantially in back-tests.'
        },
        {
          heading: 'Workflow runs ~4\u00d7 faster end-to-end.',
          text:
            'Adjudication runs in real time at FNOL and on every supplement. Cycle time on simple claims collapsed.'
        },
        {
          heading: 'Cost went from $0.29 to $0.08 per claim (72% saved).',
          text:
            'Annual inference spend dropped from ~$1.5M to ~$0.4M, freeing budget for cross-LOB rollout in the same fiscal year.'
        },
        {
          heading: 'DOI and SIU got a deterministic record.',
          text:
            'Each decision cites the exact policy form, endorsement, and fraud signal. State audit and SIU shifted from re-review to spot-check.'
        },
        {
          heading: 'Human-in-the-loop is exception-only.',
          text:
            'Adjusters now focus on ambiguous coverage and complex losses. Per-adjuster throughput rose materially without headcount changes.'
        },
        {
          heading: 'Released to production in 19 days.',
          text:
            'One claims platform engineer + one senior adjuster SME. The 7-month pilot went live in under 3 weeks across the lead LOB.'
        }
      ],
      metrics: {
        accuracy: ['74%', '96%'],
        speedup: '4\u00d7',
        cost: ['$0.29', '$0.08'],
        savedPct: '72%',
        savedAbs: '$0.21'
      },
      timeline: {
        stuck: '7 months',
        live: '19 days',
        team: '1 claims platform engineer + 1 senior adjuster SME'
      }
    },
    {
      industry: 'Legal & Compliance',
      useCase: 'Contract Review & Risk Extraction',
      complexity: 'Very High',
      hero: 'The Head of Contract Lifecycle Management at a Global Manufacturer.',
      context:
        'Her CLM function reviews ~6,500 contracts and amendments a month across procurement, customer, distributor, and licensing agreements. Each one carries obligations, risk clauses, and regulatory exposure across 40+ jurisdictions. Missed clauses become litigation; conservative reviews become deal-cycle delays.',
      beforeSummary:
        'A RAG \u201ccontract review copilot\u201d that highlighted clauses and drafted redlines. Helpful for paralegals, untrustworthy for the General Counsel\u2019s sign-off.',
      beforeStack: [
        'GPT-4 / Claude API as the reasoning engine',
        'Pinecone vector DB over the company clause library and contract corpus',
        'OpenAI embeddings + cross-encoder reranker',
        'iManage / SharePoint connectors for the contract repository',
        'LangChain orchestration with clause-type-specific prompts',
        'Spreadsheet-based obligation tracker',
        'Custom guardrails for jurisdiction-specific compliance',
        'Manual playbook in Word + clause library in OneNote',
        'Eval harness on a hand-labeled clause set'
      ],
      beforePain: [
        {
          heading: 'Latency, accuracy, and cost were all wrong.',
          text:
            'End-to-end clause review was slower than a senior paralegal, only 69% accurate against senior-counsel review, and $0.49 per transaction. Not fast enough for live deal cycles, and not accurate enough for counsel.'
        },
        {
          heading: 'Counsel never put it in their daily routine.',
          text:
            'Lawyers re-read the contract themselves anyway. The tool became a glorified search box, not a review system.'
        },
        {
          heading: 'The volume math did not justify scale.',
          text:
            '~78k contracts/yr \u00d7 $0.49 = ~$38k/mo in inference, with no measurable cycle-time win. CFO held back further investment.'
        },
        {
          heading: 'Explainability did not satisfy compliance or external auditors.',
          text:
            'Clause classifications drifted across runs; jurisdictional treatment was inconsistent. Compliance rejected any auto-redline.'
        },
        {
          heading: 'Human-in-the-loop was the whole process.',
          text:
            'Every contract still went through a paralegal and counsel. \u201cAutomation\u201d delivered marginal time savings at best.'
        },
        {
          heading: 'Stuck in pilot for 6 months.',
          text:
            'Two model swaps, one re-embedding of the corpus, three prompt rewrites. The General Counsel still would not approve auto-redline.'
        }
      ],
      afterSummary:
        'Migrated to CerebralMachines HCLC. Same contract repository, same clause library, same playbooks \u2014 with deterministic clause logic and a verifiable per-clause record.',
      afterStack: [
        'Hybrid Cognitive Linguistic Core (HCLC) as the reasoning engine',
        'Distributed semantic graph over the clause library, playbooks, and jurisdictional rules',
        'Deterministic process algorithms for clause \u2192 risk \u2192 redline logic',
        'Verifiable chain of logic per clause (counsel- and audit-readable)',
        'Automated semantic layer over iManage / SharePoint and the obligation tracker',
        'Jurisdictional rule packs as first-class graph constructs',
        'Exception-only routing into counsel review'
      ],
      afterOutcomes: [
        {
          heading: 'Accuracy went from 69% to 94%.',
          text:
            'Clause classification and risk extraction match senior-counsel review. Missed clauses dropped sharply on the validation set.'
        },
        {
          heading: 'Workflow runs ~4\u00d7 faster end-to-end.',
          text:
            'Counsel reviews surface flagged clauses immediately, with full citations. Deal cycle time dropped meaningfully across procurement and customer agreements.'
        },
        {
          heading: 'Cost went from $0.49 to $0.13 per section (73% saved).',
          text:
            'Monthly inference spend fell from ~$38k to ~$10k, with a much higher-quality output. ROI moved from negative to clearly positive in one quarter.'
        },
        {
          heading: 'Compliance and external auditors trust the trace.',
          text:
            'Each clause decision cites the playbook line and jurisdictional rule. Audit moved from re-review to sign-off.'
        },
        {
          heading: 'Human-in-the-loop is exception-only.',
          text:
            'Counsel reviews only flagged clauses, not entire contracts cold. Per-attorney throughput rose materially.'
        },
        {
          heading: 'Released to production in 25 days.',
          text:
            'One CLM platform engineer + one senior counsel SME. The 6-month pilot went live in under 4 weeks, on the same iManage and SharePoint footprint.'
        }
      ],
      metrics: {
        accuracy: ['69%', '94%'],
        speedup: '4\u00d7',
        cost: ['$0.49', '$0.13'],
        savedPct: '73%',
        savedAbs: '$0.36'
      },
      timeline: {
        stuck: '6 months',
        live: '25 days',
        team: '1 CLM platform engineer + 1 senior counsel SME'
      }
    }
  ];

  const industries = [
    {
      name: "Healthcare Providers",
      cases: [
        "Prior authorization", "Necessity review", "Risk scoring",
        "Care pathway optimization", "Clinical compliance",
        "Documentation reasoning", "UM/CM automation",
        "Readmission prevention", "Policy decoding",
        "Clinical safety guardrails"
      ]
    },
    {
      name: "Health Insurance / Payers",
      cases: [
        "Claims adjudication", "Eligibility logic", "Policy modeling",
        "Payment integrity", "Fraud/FWA detection",
        "Prior auth evaluation", "Coverage rules",
        "Out-of-network logic", "Clinical criteria alignment",
        "Appeals & denials automation"
      ]
    },
    {
      name: "Life Sciences / Pharma",
      cases: [
        "Trial protocol reasoning", "AEs classification",
        "Labeling/contraindication logic", "Regulatory pathways",
        "Safety monitoring", "Submission intelligence",
        "Manufacturing deviation logic", "Investigator eligibility",
        "Medical affairs query handling",
        "Policy-bound LLM summarization"
      ]
    },
    {
      name: "Banking",
      cases: [
        "AML reasoning", "Risk scoring", "Transaction compliance",
        "Credit adjudication", "Regulation mapping",
        "Fraud signal fusion", "Sanctions screening",
        "Loan modification approval",
        "Real-time decision guardrails",
        "Explainable advisory automation"
      ]
    },
    {
      name: "Insurance (P&C)",
      cases: [
        "Underwriting logic", "FNOL classification",
        "Subrogation detection", "Premium calculation",
        "Fraud signals", "Accident reasoning",
        "Document\u2192rule extraction",
        "Adjuster assistance",
        "Litigation triage intelligence",
        "Catastrophe modeling logic"
      ]
    },
    {
      name: "Government/Public Sector",
      cases: [
        "Benefits eligibility", "Case adjudication",
        "Regulation digitization", "Procurement risk",
        "Public safety signal fusion",
        "Licensing workflows",
        "Compliance engines",
        "Fraud detection",
        "Judicial assistance (explainable)",
        "Grant qualification modeling"
      ]
    },
    {
      name: "Legal/Compliance",
      cases: [
        "Contract obligation extraction",
        "Clause conflict detection",
        "Compliance mapping",
        "Versioned policy alignment",
        "Risk scoring",
        "SLA assurance",
        "Litigation knowledge analysis",
        "Summary generation w/ guardrails",
        "Exception reasoning",
        "Regulatory impact modeling"
      ]
    },
    {
      name: "Retail",
      cases: [
        "Dynamic pricing", "Catalog reasoning",
        "Supply allocation", "Inventory policy decisions",
        "Warranty eligibility",
        "Returns governance",
        "Promotion rule compliance",
        "Loss-prevention logic",
        "Supplier scoring",
        "Customer policy routing"
      ]
    },
    {
      name: "E-Commerce",
      cases: [
        "Refund logic", "Fulfillment routing",
        "Fraud risk scoring",
        "Product ontology mapping",
        "Knowledge unification",
        "Dispute automation",
        "Supplier trust scoring",
        "Cross-border policy interpretation",
        "Review & compliance scanning",
        "Recommendation constraints"
      ]
    },
    {
      name: "Telecommunications",
      cases: [
        "SLA reasoning", "Outage triage",
        "Device troubleshooting",
        "Billing dispute logic",
        "Network optimization",
        "Fraud intercept",
        "Field service reasoning",
        "Tariff policy evaluation",
        "Contract alignment",
        "OSS/BSS automations"
      ]
    },
    {
      name: "Energy & Utilities",
      cases: [
        "Grid stability modeling", "Rate design logic",
        "Safety compliance", "Regulatory workflows",
        "Risk-based shutdown logic",
        "Outage prioritization",
        "Field inspection intelligence",
        "Maintenance scheduling",
        "Permit workflows",
        "Infrastructure investment reasoning"
      ]
    },
    {
      name: "Oil & Gas",
      cases: [
        "EHS workflow reasoning",
        "Drilling decision logic",
        "Lease contract modeling",
        "Hazard analysis",
        "Maintenance prioritization",
        "Spill risk workflows",
        "Regulatory reconciliation",
        "Shutdown logic automation",
        "Subsurface interpretation",
        "Training & SOP reasoning"
      ]
    },
    {
      name: "Manufacturing",
      cases: [
        "Quality deviation reasoning",
        "Predictive maintenance",
        "Yield optimization",
        "Supplier contracts",
        "Safety enforcement",
        "Failure root cause reasoning",
        "Procedural extraction",
        "Line automation decisions",
        "Regulatory mapping",
        "Workforce guidance"
      ]
    },
    {
      name: "Supply Chain & Logistics",
      cases: [
        "Routing optimization",
        "Customs clearance logic",
        "Mode selection workflows",
        "Carrier scoring",
        "Damage claims review",
        "Fuel & toll optimization",
        "Delivery ETA intelligence",
        "Demand forecasting",
        "Scheduling logic",
        "Lost-shipment reasoning"
      ]
    },
    {
      name: "Aviation & Aerospace",
      cases: [
        "Flight clearance",
        "Incident triage",
        "Regulatory pathway modeling",
        "Maintenance scheduling",
        "Crew qualification checks",
        "Emergency procedure intelligence",
        "Aircraft health reasoning",
        "Airspace compliance",
        "Safety documentation extraction",
        "Schedule optimization"
      ]
    },
    {
      name: "Transportation (Rail, Maritime)",
      cases: [
        "Routing logic", "Hazmat workflows",
        "Crew qualifications",
        "Delay risk",
        "Border crossing compliance",
        "Track/asset inspection",
        "Maintenance reasoning",
        "Accident reconstruction",
        "Ticketing fraud scanning",
        "Performance/regulation reporting"
      ]
    },
    {
      name: "Education",
      cases: [
        "Admission eligibility",
        "Student risk prediction",
        "Curriculum intelligence",
        "Financial aid rules",
        "Academic guidance",
        "FERPA-safe automation",
        "Accreditation matrix",
        "Instructor optimization",
        "Policy extraction",
        "Student support automation"
      ]
    },
    {
      name: "Hospitality",
      cases: [
        "Dynamic yield logic",
        "VIP routing",
        "Pricing governance",
        "Overbooking resolution",
        "Vendor scoring",
        "Event planning intelligence",
        "Loyalty tier automation",
        "Dispute management",
        "Refund policies",
        "Maintenance reasoning"
      ]
    },
    {
      name: "Real Estate & Construction",
      cases: [
        "Permit automation",
        "Contract clause reasoning",
        "Safety enforcement",
        "scheduling optimization",
        "Bid evaluation",
        "Material allocation",
        "RFI/RFP automation",
        "Lease analysis",
        "Sustainability compliance",
        "Risk scoring"
      ]
    },
    {
      name: "Technology & SaaS",
      cases: [
        "Support reasoning",
        "IAM policy enforcement",
        "SLA compliance",
        "DevOps guidance",
        "Cost governance",
        "Security guardrails",
        "License workflows",
        "Audit pipeline",
        "Ontology consolidation",
        "LLM control layer"
      ]
    }
  ];

  return (
    <div className="use-cases-page">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="uc-hero">
        <div className="uc-container">
          <motion.div {...fadeInUp}>
            <span className="uc-hero-eyebrow">USE CASE JOURNEYS</span>
            <h1 className="uc-hero-title">
              From Stuck Pilot to Production &mdash; in Weeks, Not Years
            </h1>
            <p className="uc-hero-lede">
              Six real enterprise use cases. Each one was a high-priority program. Each one stalled
              for months on the same wall: an LLM-and-RAG architecture that looked great in demos
              and could not survive production volume, regulator scrutiny, or the operations team&rsquo;s
              daily routine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section A - Six Real Journeys */}
      <section className="uc-journeys">
        <div className="uc-container">
          <div className="uc-journeys__list">
            {journeys.map((story, index) => (
              <JourneyStory key={index} story={story} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Section B - Industry Applicability */}
      <section className="uc-section uc-section-dark">
        <div className="uc-container">
          <motion.div {...fadeInUp}>
            <span className="uc-eyebrow">INDUSTRY APPLICABILITY</span>
            <h2 className="uc-section-title">Beyond the Six Stories &mdash; Where Else CerebralMachines Applies</h2>
            <p className="uc-section-subtitle">
              The same HCLC architecture that powers the journeys above generalizes to the top
              enterprise industries and the high-value use cases inside each one. Browse the landscape
              below to see where your operating model fits.
            </p>

            <div className="uc-industries-grid">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  className="uc-industry-card"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.03 }}
                >
                  <h3 className="uc-industry-name">{industry.name}</h3>
                  <ol className="uc-industry-list">
                    {industry.cases.map((useCase, idx) => (
                      <li key={idx}>{useCase}</li>
                    ))}
                  </ol>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="uc-cta">
        <div className="uc-container">
          <motion.div {...fadeInUp} className="uc-cta-content">
            <h2 className="uc-cta-title">Ready to Move Your Stuck Pilot to Production?</h2>
            <p className="uc-cta-text">
              Bring us the use case that has been stuck for months. We will scope a CerebralMachines
              journey, on your data, with your SMEs, and your audit and risk teams in the room.
            </p>
            <div className="uc-cta-buttons">
              <button className="btn-primary-large" onClick={() => navigate('/request-demo')}>Request a Demo</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">
                Building the cognitive backbone for enterprise AI.
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>

              <div className="footer-column">
                <h4 className="footer-heading">Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default UseCasesPage;
